import { useEffect, useRef } from "react";

// Fixed full-viewport canvas rendering:
//  - twinkling background stars (pulsing opacity)
//  - soft falling particles drifting downward with gentle horizontal sway
// Glow orbs are rendered separately as CSS-animated blurred divs (see below)
// for a softer, more organic drift than canvas would give cheaply.
const STAR_COUNT_DESKTOP = 110;
const STAR_COUNT_MOBILE = 55;
const PARTICLE_COUNT_DESKTOP = 16;
const PARTICLE_COUNT_MOBILE = 10;

// Pure factory + draw helpers, extracted from the animation effect body
// so the star field and falling-particle logic are each independently
// readable/testable rather than one large inline closure.
const createStars = (count, width, height) =>
  Array.from({ length: count }, () => ({
    x: Math.random() * width,
    y: Math.random() * height,
    r: Math.random() * 1.5 + 1,
    phase: Math.random() * Math.PI * 2,
    speed: Math.random() * 0.02 + 0.008,
  }));

const createParticles = (count, width, height) =>
  Array.from({ length: count }, () => ({
    x: Math.random() * width,
    y: Math.random() * height,
    r: Math.random() * 1.2 + 0.8,
    vy: Math.random() * 0.28 + 0.12,
    swaySpeed: Math.random() * 0.6 + 0.2,
    swayAmp: Math.random() * 16 + 6,
    swayPhase: Math.random() * Math.PI * 2,
    opacity: Math.random() * 0.2 + 0.12,
  }));

// Twinkling stars (opacity range 0.12 - 0.32 per spec).
const drawStars = (ctx, stars, t) => {
  stars.forEach((s) => {
    const twinkle = 0.12 + 0.2 * Math.abs(Math.sin(s.phase + t * s.speed));
    ctx.beginPath();
    ctx.fillStyle = `rgba(211,218,223,${twinkle})`;
    ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
    ctx.fill();
  });
};

// Falling particles -- mutates each particle's position in place and
// wraps it back to the top/sides once it drifts off-screen.
const drawParticles = (ctx, particles, t, width, height) => {
  particles.forEach((p) => {
    const sway = Math.sin(t * 0.01 * p.swaySpeed + p.swayPhase) * p.swayAmp * 0.02;
    p.x += sway;
    p.y += p.vy;
    if (p.y > height + 5) {
      p.y = -5;
      p.x = Math.random() * width;
    }
    if (p.x > width + 10) p.x = -10;
    if (p.x < -10) p.x = width + 10;

    ctx.beginPath();
    ctx.fillStyle = `rgba(211,218,223,${p.opacity})`;
    ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
    ctx.fill();
  });
};

const Atmosphere = () => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    let width = window.innerWidth;
    let height = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const isMobile = width < 768;
    const starCount = isMobile ? STAR_COUNT_MOBILE : STAR_COUNT_DESKTOP;
    const particleCount = isMobile ? PARTICLE_COUNT_MOBILE : PARTICLE_COUNT_DESKTOP;

    const setSize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    setSize();

    const stars = createStars(starCount, width, height);
    const particles = createParticles(particleCount, width, height);

    let t = 0;

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      drawStars(ctx, stars, t);
      drawParticles(ctx, particles, t, width, height);
      t += 1;
      rafRef.current = requestAnimationFrame(draw);
    };

    if (!prefersReduced) {
      rafRef.current = requestAnimationFrame(draw);
    } else {
      draw();
    }

    const onResize = () => setSize();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true">
      <canvas ref={canvasRef} className="absolute inset-0" />
      <div className="glow-orb glow-orb-1" />
      <div className="glow-orb glow-orb-2" />
      <div className="glow-orb glow-orb-3" />
    </div>
  );
};

export default Atmosphere;
